import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLangMap } from '../../hooks/useLanguages';
import { LearningProgressService } from '../../services/LearningProgressService';
import { fetchData } from '../../services/dataApi';
import {
  hasDistinctNativeStory,
  mergeStoriesWithNative,
} from '../../services/storyLocalization';

const LEVEL_META = {
  easy:      { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  medium:    { color: 'bg-amber-100 text-amber-700 border-amber-200',       dot: 'bg-amber-500'   },
  difficult: { color: 'bg-rose-100 text-rose-700 border-rose-200',          dot: 'bg-rose-500'    },
};

const LEGACY_STORY_PATHS = new Set([
  'medium/4.json',
  'medium/6.json',
  'difficult/5.json',
  'difficult/7.json',
]);

const hasLegacyStoryPaths = (index) => index.some((story) => LEGACY_STORY_PATHS.has(story.file));

async function loadStoriesForLanguage(langCode) {
  const indexPath = `stories/${langCode}/index.json`;
  const initialIndex = await fetchData(indexPath);
  const index = hasLegacyStoryPaths(initialIndex)
    ? await fetchData(indexPath, { force: true })
    : initialIndex;
  return Promise.all(
    index.map((story) => fetchData(`stories/${langCode}/${story.file}`)),
  );
}

async function loadNativeStories(langCode) {
  try {
    return await loadStoriesForLanguage(langCode);
  } catch (error) {
    if (langCode === 'en') throw error;
    return loadStoriesForLanguage('en');
  }
}

function useStories(langCode, nativeCode) {
  const [stories, setStories] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!langCode) return;
    let active = true;
    setLoading(true);
    setError(false);
    setStories(null);
    const targetPromise = loadStoriesForLanguage(langCode);
    Promise.all([
      targetPromise,
      nativeCode === langCode ? targetPromise : loadNativeStories(nativeCode || 'en'),
    ])
      .then(([targetStories, nativeStories]) => {
        if (!active) return;
        setStories(mergeStoriesWithNative(targetStories, nativeStories));
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });
    return () => { active = false; };
  }, [langCode, nativeCode]);

  return { stories, loading, error };
}

function StoryReader({
  story,
  targetDir = 'ltr',
  nativeDir = 'ltr',
  targetLabel,
  nativeLabel,
  onClose,
}) {
  const { t } = useTranslation();
  const level = LEVEL_META[story.level] ?? LEVEL_META.easy;
  const hasNative = hasDistinctNativeStory(story);
  const titleId = `story-reader-title-${story.id}`;
  const levelLabel = story.level === 'easy' ? t('stories.easy')
    : story.level === 'medium' ? t('stories.medium')
    : t('stories.difficult');

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto overscroll-contain bg-black/40 px-4 backdrop-blur-sm sm:items-center"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        paddingTop: 'max(1rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-white shadow-2xl animate-fade-in"
      >
        <div
          className="shrink-0 px-6 pb-5 pt-6"
          style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%)' }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-4xl mb-2">{story.emoji}</p>
              <h2 id={titleId} dir={targetDir} className="text-xl font-black text-white leading-tight">{story.title}</h2>
              {hasNative && story.nativeTitle && (
                <p dir={nativeDir} className="mt-1 text-sm font-semibold leading-relaxed text-white/75">
                  {story.nativeTitle}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 rounded-xl bg-white/20 p-2 text-white hover:bg-white/30 transition"
              aria-label={t('common.close')}
              autoFocus
            >
              ✕
            </button>
          </div>
          <span className={`mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${level.color}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${level.dot}`} />
            {levelLabel}
          </span>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-6 py-5">
          <section>
            <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-indigo-500">
              {targetLabel}
            </p>
            <p dir={targetDir} className="whitespace-pre-wrap text-base leading-relaxed text-slate-700">
              {story.content}
            </p>
          </section>
          {hasNative && (
            <section className="rounded-2xl border border-violet-100 bg-violet-50 p-4">
              <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-violet-600">
                {nativeLabel}
              </p>
              {story.nativeTitle && (
                <h3 dir={nativeDir} className="mb-2 text-sm font-bold text-violet-950">
                  {story.nativeTitle}
                </h3>
              )}
              <p dir={nativeDir} className="whitespace-pre-wrap text-sm leading-relaxed text-violet-900">
                {story.nativeContent}
              </p>
            </section>
          )}
        </div>

        <div className="shrink-0 px-6 pb-6">
          <button
            onClick={onClose}
            className="w-full rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 py-3.5 text-sm font-black text-white shadow-lg hover:opacity-90 transition"
          >
            {t('stories.done')}
          </button>
        </div>
      </div>
    </div>
  );
}

function StoryCard({
  story,
  targetDir = 'ltr',
  nativeDir = 'ltr',
  onOpen,
}) {
  const { t } = useTranslation();
  const level = LEVEL_META[story.level] ?? LEVEL_META.easy;
  const levelLabel = story.level === 'easy' ? t('stories.easy')
    : story.level === 'medium' ? t('stories.medium')
    : t('stories.difficult');
  const preview = story.content.length > 90 ? story.content.slice(0, 90) + '…' : story.content;
  const hasNative = hasDistinctNativeStory(story);
  const nativePreview = story.nativeContent?.length > 90
    ? `${story.nativeContent.slice(0, 90)}…`
    : story.nativeContent;
  return (
    <button
      onClick={() => onOpen(story)}
      className="group w-full text-left rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all"
    >
      <div className="flex items-start gap-4">
        <span className="text-3xl flex-shrink-0">{story.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 dir={targetDir} className="font-black text-slate-900 text-sm">{story.title}</h3>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${level.color}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${level.dot}`} />
              {levelLabel}
            </span>
          </div>
          <p dir={targetDir} className="text-xs text-slate-500 leading-relaxed">{preview}</p>
          {hasNative && (
            <div className="mt-2 border-t border-slate-100 pt-2">
              {story.nativeTitle && (
                <p dir={nativeDir} className="text-xs font-bold text-violet-800">
                  {story.nativeTitle}
                </p>
              )}
              <p dir={nativeDir} className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
                {nativePreview}
              </p>
            </div>
          )}
        </div>
        <span className="flex-shrink-0 text-slate-300 group-hover:text-indigo-400 transition text-lg">→</span>
      </div>
    </button>
  );
}

export default function StoriesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const { langMap } = useLangMap();
  const targetCode = languagePrefs?.targetLanguage || 'en';
  const nativeCode = languagePrefs?.nativeLanguage || 'en';
  const meta = langMap[targetCode] ?? { label: targetCode?.toUpperCase() ?? '—' };
  const nativeMeta = langMap[nativeCode] ?? { label: nativeCode?.toUpperCase() ?? '—' };
  const targetDir = meta.dir || 'ltr';
  const nativeDir = nativeMeta.dir || 'ltr';

  const { stories, loading, error } = useStories(targetCode, nativeCode);
  const [open, setOpen] = useState(null);
  const openStory = (story) => {
    const pair = languagePrefs
      ? `${languagePrefs.nativeLanguage}_${languagePrefs.targetLanguage}`
      : `en_${targetCode}`;
    LearningProgressService.record(pair, 'stories', 1, {
      activityId: `story:${story.id}`,
    });
    setOpen(story);
  };

  const easy      = stories?.filter((s) => s.level === 'easy')      ?? [];
  const medium    = stories?.filter((s) => s.level === 'medium')    ?? [];
  const difficult = stories?.filter((s) => s.level === 'difficult') ?? [];

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at 20% 0%, #e0e7ff 0%, #f8fafc 60%, #fdf4ff 100%)' }}>
      {open && (
        <StoryReader
          story={open}
          targetDir={targetDir}
          nativeDir={nativeDir}
          targetLabel={meta.label}
          nativeLabel={nativeMeta.label}
          onClose={() => setOpen(null)}
        />
      )}

      <div className="mx-auto max-w-lg px-4 py-10 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-slate-400">{t('stories.title')}</p>
            <h1 className="text-2xl font-black text-slate-900">
              {meta.label}
            </h1>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50 transition"
          >
            {t('stories.back')}
          </button>
        </div>

        {/* Intro card */}
        <div
          className="rounded-3xl p-6 shadow-xl shadow-indigo-400/15"
          style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%)' }}
        >
          <p className="text-4xl mb-3">📚</p>
          <h2 className="text-lg font-black text-white mb-1">{t('stories.readAndLearn')}</h2>
          <p className="text-sm text-white/80 leading-relaxed">
            {t('stories.desc', { lang: meta.label })}
          </p>
          <div className="mt-4 flex gap-2 flex-wrap">
            {[t('stories.easyCount'), t('stories.mediumCount'), t('stories.difficultCount')].map((txt) => (
              <span key={txt} className="rounded-full bg-white/20 px-3 py-1 text-xs font-bold text-white">
                {txt}
              </span>
            ))}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-center">
            <p className="text-2xl mb-2">📭</p>
            <p className="text-sm font-bold text-rose-700">{t('stories.notAvailable')}</p>
          </div>
        )}

        {stories && (
          <>
            {[
              [t('stories.easy'),      easy],
              [t('stories.medium'),    medium],
              [t('stories.difficult'), difficult],
            ].map(([heading, list]) =>
              list.length > 0 && (
                <section key={heading}>
                  <p className="mb-3 text-xs font-black uppercase tracking-widest text-slate-400">{heading}</p>
                  <div className="space-y-3">
                    {list.map((s) => (
                      <StoryCard
                        key={s.id}
                        story={s}
                        targetDir={targetDir}
                        nativeDir={nativeDir}
                        onOpen={openStory}
                      />
                    ))}
                  </div>
                </section>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}
